/**
 * The month and year views, projected once for every renderer that draws them.
 *
 * Two renderers grew these views separately, with the same seven class names and no role between
 * them: a keyboard user met a grid of days in one view and a run of bare buttons in the next, with
 * nothing saying which month was chosen. Since a calendar is already a `grid`, so are the views that
 * replace it — the structure a user meets does not change with the view.
 */
import { given } from "../given.js";
import { MDY_WIDGET_CONTRACTS } from "../catalog.js";
import { partClasses } from "../part-classes.js";
/** The container of whichever view is showing, or null while the day grid is. */
export function projectCalendarViewA11y(mode, options) {
    if (mode === "days")
        return null;
    const part = mode === "months" ? "monthPicker" : "yearPicker";
    return {
        id: `${options.widgetId}__${mode}`,
        classes: [...MDY_WIDGET_CONTRACTS[options.kind].parts[part].classes],
        attributes: {
            role: "grid",
            "aria-labelledby": options.labelledBy ?? `${options.widgetId}__label`,
        },
    };
}
/**
 * One cell of the month or year view.
 *
 * `aria-selected` rather than a class alone: which month is chosen was carried by
 * `--selected` and nothing else, so it was visible and unannounced.
 */
export function projectCalendarPeriodCellA11y(mode, cell, options) {
    const part = mode === "months" ? "monthCell" : "yearCell";
    return {
        classes: [
            // Only `selected`: a refused period wears the native `disabled`, which the themes style, and
            // a class nothing paints is a promise to a theme author that nothing keeps.
            ...partClasses(options.kind, part, { selected: cell.selected }),
        ],
        attributes: {
            role: "gridcell",
            "aria-selected": String(cell.selected),
            // A period the bounds refuse is announced as unavailable rather than merely greyed: the
            // native `disabled` also takes it out of the tab order, which is what a grid expects.
            "aria-disabled": String(cell.disabled),
            disabled: cell.disabled,
            ...(cell.focused ? { tabindex: "0" } : { tabindex: "-1" }),
        },
    };
}
/**
 * One day of the month view.
 *
 * The sibling above covers a month and a year cell; the day had no projection at all, so three
 * renderers wrote its semantics by hand and answered differently. One of them said which day is
 * today and two said nothing — while `today` has been a declared state on this part the whole time,
 * so the contract named it and no renderer could be checked against it.
 *
 * **`aria-current="date"` is what says "today" to a reader.** A class marks it for the eye; a person
 * hearing the grid gets thirty-one numbers and no anchor without this, and today is the one date a
 * calendar is always asked about.
 *
 * `selected` and `disabled` come with it because they are the same cell's answers and were three
 * hand-written pairs before — the shape that produced a cell announced as selected in one renderer
 * and merely painted in another.
 */
export function projectCalendarDayCellA11y(cell, options) {
    given("projectCalendarDayCellA11y", "{ selected, disabled, today, focused, outside }", cell, ["selected", "disabled", "today", "focused", "outside"]);
    return {
        classes: [
            ...partClasses(options.kind, "gridcell", {
                selected: cell.selected,
                today: cell.today,
                outside: cell.outside,
                focused: cell.focused,
            }),
        ],
        attributes: {
            role: "gridcell",
            "aria-selected": String(cell.selected),
            "aria-disabled": String(cell.disabled),
            disabled: cell.disabled,
            // The one date a calendar is always asked about, said rather than only painted. `date` and not
            // `true`: the token names what kind of current this is, which is what a reader announces.
            ...(cell.today ? { "aria-current": "date" } : {}),
            tabindex: cell.focused ? "0" : "-1",
        },
    };
}
